import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import { FrequentlyBoughtTogether } from '../components/FrequentlyBoughtTogether';

export const Cart = () => {
  const { cart, updateQuantity, removeFromCart, cartTotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const shipping = cartTotal >= 500 ? 0 : 50;
  const tax = Math.round(cartTotal * 0.05);
  const total = cartTotal + shipping + tax;

  const handleCheckout = () => {
    if (!user) {
      navigate('/login?redirect=/checkout');
    } else {
      navigate('/checkout');
    }
  };

  if (cart.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 text-center">
        <ShoppingBag size={64} className="mx-auto text-gray-300 mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Your cart is empty</h2>
        <p className="text-gray-500 mb-6">Add some puja items to get started</p>
        <Link to="/products">
          <Button className="bg-orange-600 hover:bg-orange-700">
            Browse Products
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-orange-800 mb-8">Shopping Cart</h1>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-4">
          <FrequentlyBoughtTogether cart={cart} />
          {cart.map((item) => (
            <div key={item.product_id} className="bg-white rounded-xl p-4 shadow-sm flex gap-4" data-testid={`cart-item-${item.product_id}`}>
              <img
                src={item.product_image || 'https://images.pexels.com/photos/5702290/pexels-photo-5702290.jpeg?w=100'}
                alt={item.product_name}
                className="w-24 h-24 object-cover rounded-lg"
              />
              <div className="flex-1">
                <Link to={`/product/${item.product_id}`} className="font-medium text-gray-800 hover:text-orange-600">
                  {item.product_name}
                </Link>
                <p className="text-orange-600 font-bold mt-1">₹{item.price}</p>
                
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center border rounded-lg">
                    <button
                      onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                      className="p-2 hover:bg-gray-100"
                    >
                      <Minus size={16} />
                    </button>
                    <span className="px-3 font-medium">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                      className="p-2 hover:bg-gray-100"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-bold">₹{item.price * item.quantity}</span>
                    <button
                      onClick={() => removeFromCart(item.product_id)}
                      className="text-red-500 hover:text-red-700 p-2"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <button
            onClick={clearCart}
            className="text-sm text-red-500 hover:underline"
          >
            Clear Cart
          </button>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl p-6 shadow-sm sticky top-24">
            <h3 className="text-lg font-bold mb-4">Order Summary</h3>
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span>₹{cartTotal}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Shipping</span>
                <span>{shipping === 0 ? <span className="text-green-600">FREE</span> : `₹${shipping}`}</span>
              </div>
              {shipping > 0 && (
                <p className="text-xs text-orange-600">Add ₹{500 - cartTotal} more for free shipping!</p>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Tax (5% GST)</span>
                <span>₹{tax}</span>
              </div>
              <div className="border-t pt-3 flex justify-between font-bold text-lg">
                <span>Total</span>
                <span className="text-orange-600">₹{total}</span>
              </div>
            </div>

            <Button
              onClick={handleCheckout}
              className="w-full mt-6 bg-orange-600 hover:bg-orange-700"
              size="lg"
              data-testid="checkout-btn"
            >
              Proceed to Checkout <ArrowRight className="ml-2" size={18} />
            </Button>

            <div className="mt-4 text-center">
              <a
                href={`https://wa.me/919831072381?text=Hi, I want to order: ${cart.map(i => `${i.quantity}x ${i.product_name}`).join(', ')}. Total: ₹${total}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-600 text-sm hover:underline"
              >
                Or order via WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
